'use client'
import {
  Col,
  Row,
  Typography,
  Badge,
  Icon,
  NumberUnit,
} from '@repo/ui/components'
import { useIsDesktop } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import { NumberComponentType, TimePeriod } from './dashboard.types'
import { ValueWithUnit, PaymentType } from '@repo/shared/components'
import { MessagesKeys, useTranslate } from '@repo/shared/hooks'

export interface MetricCardProps {
  title: string
  valueNumber: number
  valueUnit: string
  componentType: NumberComponentType
  percentageChange: number
  showInfoIcon?: boolean
  timePeriod?: TimePeriod
}

export const MetricCard = ({
  title,
  valueNumber,
  valueUnit,
  componentType,
  percentageChange,
  showInfoIcon = false,
  timePeriod,
}: MetricCardProps) => {
  const isDesktop = useIsDesktop()
  const { t } = useTranslate()
  return (
    <div
      className={cn(
        'p-lg rounded-0 md:rounded-md bg-neutral-surface-light flex flex-col gap-md  border border-neutral-surface-dark ',
        isDesktop && ' shadow-md shadow-black-5',
      )}
    >
      <Col className='gap-md'>
        <Row className='justify-between gap-xs flex-col md:flex-row'>
          <Row className='items-center w-fit gap-2xs'>
            <Typography
              size='sm'
              weight='bold'
              color='text-neutral-surface-100'
            >
              {t(title as MessagesKeys)}
            </Typography>
            {showInfoIcon && (
              <Icon name='InfoCircle' size='xs' color='fill-base-surface-100' />
            )}
          </Row>
          {componentType === 'NumberUnit' && (
            <NumberUnit
              value={valueNumber}
              size='xs'
              numberSize='xl'
              isBold={true}
              unit={t(valueUnit as MessagesKeys)}
            />
          )}
          {componentType === 'ValueWithUnit' && (
            <ValueWithUnit
              value={valueNumber}
              unitSize='xs'
              valueSize='xl'
              unit='property'
              valueWeight='bold'
            />
          )}
          {componentType === 'PaymentType' && (
            <PaymentType
              value={valueNumber}
              size='xs'
              numberSize='xl'
              isBold={true}
            />
          )}
        </Row>

        <Row className='gap-2xs justify-between flex-col md:flex-row'>
          {isDesktop && (
            <Typography
              size='2xs'
              color='text-neutral-surface-80'
              className='hidden md:block'
            >
              {t('dashboardAgent_change_from_previous_period' as MessagesKeys) +
                ' ' +
                t(`dashboardAgent_${timePeriod}` as MessagesKeys)}
            </Typography>
          )}

          <Badge
            variant='text'
            size='md'
            color={percentageChange >= 0 ? 'positive' : 'negative'}
            className='w-fit'
          >
            <span className='ltr px-2xs'>%</span>
            <span className='ltr'>
              {percentageChange > 0 ? '+' : ''}
              {Math.abs(percentageChange).toFixed(2)}
            </span>
            <Icon
              name='TriangleDown'
              size='xs'
              className={percentageChange >= 0 ? 'rotate-180' : ''}
              color={
                percentageChange >= 0
                  ? 'fill-positive-surface-100'
                  : 'fill-negative-surface-100'
              }
            />
          </Badge>
        </Row>
      </Col>
    </div>
  )
}
